import { useState, useEffect } from 'react';
import type { Module } from '@/shared/schemas';
import type { CatalogSource } from '@/shared/types';
import { fetchCatalog, logCatalogSource } from '../api/fetch-catalog';

interface UseCatalogResult {
  readonly modules: readonly Module[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly source: CatalogSource | null;
}

/**
 * Hook to fetch and manage the module catalog.
 * Handles loading state, errors, and API fallback.
 */
export function useCatalog(): UseCatalogResult {
  const [modules, setModules] = useState<readonly Module[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [source, setSource] = useState<CatalogSource | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await fetchCatalog();

        if (cancelled) return;

        setModules(response.modules);
        setSource(response.source);
        logCatalogSource(response.source);
      } catch (err) {
        if (cancelled) return;

        setError(err instanceof Error ? err : new Error('Failed to load catalog'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  return { modules, isLoading, error, source };
}
